import React, { FC, ReactNode, useMemo, useState } from 'react';
import styles from '../journey-explorer.module.scss';
import { GoalOutcome, JourneyFilterState, JourneyMeta } from '../../../../models/journey-explorer/journey-explorer';
import {
	COLORS,
	FlowSettings,
	GOAL_OUTCOME_LABELS,
	dataLimits,
	goalOf,
	goalOutcomeOf,
	interactionDimensions,
	searchDimensions,
	withGoalOutcome,
} from '../journey-explorer-utils';
import { InteractionInfo, resolveInfo } from '../journey-map-layout';
import { isSimplified, simplifyOf } from '../journey-simplify';
import { JourneySource } from '../journey-api-context';
import CustomerFilterGroup, { CUSTOMER_GROUP_LABEL } from './customer-filter-group';
import DateRange from './date-range';
import FlowControls from './flow-controls';
import InteractionDetailGroup, { DETAIL_GROUP_LABEL } from './interaction-detail-group';
import SimplifyControls from './simplify-controls';

const CUSTOMER_GROUP = 'customer';
const DETAIL_GROUP = 'interaction-details';
const SIMPLIFY_SECTION = 'section:simplify';

interface SidebarSectionProps {
	title: string;
	summary: string;
	open: boolean;
	onToggle: () => void;
	children: ReactNode;
}

const SidebarSection: FC<SidebarSectionProps> = ({ title, summary, open, onToggle, children }) => (
	<>
		<button type="button" className={styles.section_toggle} aria-expanded={open} onClick={onToggle}>
			<span className={`${styles.chevron} ${open ? styles.chevron_down : ''}`} />
			{title}
			{!open && summary && <span className={styles.section_summary}>{summary}</span>}
		</button>
		{open && children}
	</>
);

interface SidebarProps {
	meta: JourneyMeta;
	viewMeta: JourneyMeta;
	lookup: Map<string, InteractionInfo>;
	filterState: JourneyFilterState;
	flowActive: boolean;
	flowSettings: FlowSettings;
	collapsed: boolean;
	filterCount: number;
	onFilterChange: (state: JourneyFilterState) => void;
	onFlowChange: (patch: Partial<FlowSettings>) => void;
	onToggleCollapsed: () => void;
	refreshing: boolean;
	onRefresh: () => void;
	source: JourneySource;
	onSourceChange: (source: JourneySource) => void;
	goalOptions: string[];
	onGoalChange: (goal: string | null) => void;
	onResetFilters: () => void;
}

const OUTCOMES: { outcome: GoalOutcome | null; label: string }[] = [
	{ outcome: null, label: 'All' },
	{ outcome: 'reached', label: 'Reached' },
	{ outcome: 'not_reached', label: 'Not reached' },
];

const SOURCES: { source: JourneySource; label: string }[] = [
	{ source: 'website', label: 'Website' },
	{ source: 'subscription', label: 'Subscription' },
];

const RefreshIcon: FC = () => (
	<svg
		width="15"
		height="15"
		viewBox="0 0 24 24"
		fill="none"
		stroke="currentColor"
		strokeWidth="2"
		strokeLinecap="round"
		strokeLinejoin="round"
	>
		<path d="M21 12a9 9 0 1 1-2.64-6.36" />
		<path d="M21 3v6h-6" />
	</svg>
);

const PanelIcon: FC = () => (
	<svg
		width="15"
		height="15"
		viewBox="0 0 15 15"
		fill="none"
		stroke="currentColor"
		strokeWidth="1.5"
		strokeLinejoin="round"
	>
		<rect x="1.75" y="2.25" width="11.5" height="10.5" rx="2.25" />
		<path d="M5.75 2.25v10.5" />
	</svg>
);

const Sidebar: FC<SidebarProps> = ({
	meta,
	viewMeta,
	lookup,
	filterState,
	flowActive,
	flowSettings,
	collapsed,
	filterCount,
	onFilterChange,
	onFlowChange,
	onToggleCollapsed,
	refreshing,
	onRefresh,
	source,
	onSourceChange,
	goalOptions,
	onGoalChange,
	onResetFilters,
}) => {
	const [openGroups, setOpenGroups] = useState<Set<string>>(
		() => new Set(isSimplified(simplifyOf(filterState)) ? [SIMPLIFY_SECTION] : [])
	);
	const limits = dataLimits(meta);
	const simplify = simplifyOf(filterState);
	const simplifySummary = [
		simplify.hidden.length > 0 && `${simplify.hidden.length} hidden`,
		simplify.groups.length > 0 && `${simplify.groups.length} ${simplify.groups.length === 1 ? 'group' : 'groups'}`,
	]
		.filter(Boolean)
		.join(' · ');
	const label = (name: string | null) => (name ? resolveInfo(lookup, name).label : '');
	const goal = goalOf(filterState);
	const outcome = goalOutcomeOf(filterState);
	const details = useMemo(() => interactionDimensions(meta), [meta]);
	const [filterSearch, setFilterSearch] = useState('');
	const searching = filterSearch.trim() !== '';
	const customerMatches = useMemo(
		() => searchDimensions(meta.customer_dimensions, filterSearch, CUSTOMER_GROUP_LABEL),
		[meta.customer_dimensions, filterSearch]
	);
	const detailMatches = useMemo(
		() => searchDimensions(details, filterSearch, DETAIL_GROUP_LABEL),
		[details, filterSearch]
	);

	const [openFilter, setOpenFilter] = useState<string | null>(null);
	const toggleFilter = (group: string) => setOpenFilter((current) => (current === group ? null : group));

	const toggleGroup = (group: string) =>
		setOpenGroups((current) => {
			const next = new Set(current);
			if (!next.delete(group)) {
				next.add(group);
			}
			return next;
		});

	return (
		<aside className={`${styles.sidebar} ${collapsed ? styles.sidebar_collapsed : ''}`}>
			<div className={styles.sidebar_header}>
				{!collapsed && (
					<div
						className={`${styles.segmented} ${styles.source_switch}`}
						role="group"
						aria-label="Data source"
					>
						{SOURCES.map((option) => (
							<button
								key={option.source}
								type="button"
								aria-pressed={source === option.source}
								className={`${styles.segment} ${source === option.source ? styles.segment_active : ''}`}
								onClick={() => onSourceChange(option.source)}
							>
								{option.label}
							</button>
						))}
					</div>
				)}
				<div className={styles.sidebar_header_actions}>
					{!collapsed && (
						<button
							type="button"
							className={`${styles.sidebar_toggle} ${refreshing ? styles.refresh_spinning : ''}`}
							aria-label="Refresh data"
							title="Refresh data: reload the latest data, skipping saved results"
							disabled={refreshing}
							onClick={onRefresh}
						>
							<RefreshIcon />
						</button>
					)}
					<button
						type="button"
						className={styles.sidebar_toggle}
						aria-expanded={!collapsed}
						aria-label={collapsed ? 'Show filters' : 'Hide filters'}
						title={collapsed ? 'Show filters' : 'Hide filters'}
						onClick={onToggleCollapsed}
					>
						<PanelIcon />
					</button>
				</div>
			</div>
			{collapsed && filterCount > 0 && (
				<span
					className={styles.sidebar_badge}
					title={`${filterCount} ${filterCount === 1 ? 'filter' : 'filters'} active`}
				>
					{filterCount}
				</span>
			)}
			<div className={collapsed ? styles.view_hidden : styles.sidebar_content}>
				<div className={styles.sidebar_scroll}>
					{source === 'subscription' && (
						<span className={styles.source_note}>Demo data, for preview only</span>
					)}
					<section
						className={`${styles.goal_card} ${goal ? styles.goal_card_set : ''}`}
						aria-labelledby="journey-goal-title"
					>
						<div className={styles.goal_card_head}>
							<span className={styles.goal_card_icon} aria-hidden="true">
								<svg
									viewBox="0 0 16 16"
									width="14"
									height="14"
									fill="none"
									stroke="currentColor"
									strokeWidth="1.6"
								>
									<circle cx="8" cy="8" r="6.2" />
									<circle cx="8" cy="8" r="3.2" />
									<circle cx="8" cy="8" r="0.6" fill="currentColor" />
								</svg>
							</span>
							<span>
								<label
									id="journey-goal-title"
									className={styles.goal_card_title}
									htmlFor="journey-goal"
								>
									Goal
								</label>
								<span className={styles.goal_card_caption}>Every view is measured against it</span>
							</span>
						</div>
						<select
							id="journey-goal"
							className={`${styles.flow_select} ${styles.goal_card_select}`}
							value={goal ?? ''}
							onChange={(event) => onGoalChange(event.target.value || null)}
						>
							<option value="">No goal</option>
							{goalOptions.map((name) => (
								<option key={name} value={name}>
									{label(name)}
								</option>
							))}
						</select>
						{goal && (
							<div className={styles.segmented} role="group" aria-label="Goal outcome">
								{OUTCOMES.map((option) => (
									<button
										key={option.label}
										type="button"
										aria-pressed={outcome === option.outcome}
										title={option.outcome ? GOAL_OUTCOME_LABELS[option.outcome] : 'All customers'}
										className={`${styles.segment} ${
											outcome === option.outcome ? styles.segment_active : ''
										}`}
										onClick={() => onFilterChange(withGoalOutcome(filterState, option.outcome))}
									>
										{option.label}
									</button>
								))}
							</div>
						)}
						<span className={styles.goal_card_note}>
							{goal
								? 'Steps and lines count when the customer reaches the goal afterwards.'
								: 'Pick the interaction that counts as success to see goal rates everywhere.'}
						</span>
					</section>
					<div className={styles.date_ranges}>
						<DateRange
							title="Customer start date"
							range={filterState.customer_start}
							limits={limits}
							color={COLORS.accent}
							onChange={(customer_start) => onFilterChange({ ...filterState, customer_start })}
						/>
						<DateRange
							title="Interaction date"
							range={filterState.interaction_range}
							limits={limits}
							color={COLORS.accent}
							onChange={(interaction_range) => onFilterChange({ ...filterState, interaction_range })}
						/>
					</div>
					<div className={`${styles.filters_heading} ${styles.filters_heading_row}`}>
						Filters
						<span className={styles.filter_search}>
							<svg
								className={styles.filter_search_icon}
								viewBox="0 0 16 16"
								width="12"
								height="12"
								fill="none"
								stroke="currentColor"
								strokeWidth="1.8"
								aria-hidden="true"
							>
								<circle cx="7" cy="7" r="4.8" />
								<path d="M10.6 10.6 14 14" strokeLinecap="round" />
							</svg>
							<input
								type="search"
								className={styles.filter_search_input}
								placeholder="Search filters"
								aria-label="Search filter names and values"
								value={filterSearch}
								onChange={(event) => setFilterSearch(event.target.value)}
								onKeyDown={(event) => event.key === 'Escape' && setFilterSearch('')}
							/>
							{searching && (
								<button
									type="button"
									className={styles.filter_search_clear}
									aria-label="Clear search"
									onClick={() => setFilterSearch('')}
								>
									×
								</button>
							)}
						</span>
						{filterCount > 0 && (
							<button
								type="button"
								className={styles.filters_reset}
								title="Remove every filter and go back to the default dates"
								onClick={onResetFilters}
							>
								Reset
							</button>
						)}
					</div>
					{customerMatches.length > 0 && (
						<CustomerFilterGroup
							dimensions={customerMatches}
							filterState={filterState}
							open={searching || openFilter === CUSTOMER_GROUP}
							search={filterSearch}
							onToggle={() => toggleFilter(CUSTOMER_GROUP)}
							onChange={onFilterChange}
						/>
					)}
					{detailMatches.length > 0 && (
						<InteractionDetailGroup
							dimensions={detailMatches}
							filterState={filterState}
							open={searching || openFilter === DETAIL_GROUP}
							search={filterSearch}
							onToggle={() => toggleFilter(DETAIL_GROUP)}
							onChange={onFilterChange}
						/>
					)}
					{searching && customerMatches.length === 0 && detailMatches.length === 0 && (
						<span className={styles.filter_search_empty}>
							No filter names or values match “{filterSearch.trim()}”
						</span>
					)}
					<div className={styles.flow_section}>
						<FlowControls
							meta={viewMeta}
							lookup={lookup}
							settings={flowSettings}
							active={flowActive}
							onChange={onFlowChange}
						/>
					</div>
					<SidebarSection
						title="Simplify"
						summary={simplifySummary}
						open={openGroups.has(SIMPLIFY_SECTION)}
						onToggle={() => toggleGroup(SIMPLIFY_SECTION)}
					>
						<SimplifyControls
							meta={meta}
							lookup={lookup}
							filterState={filterState}
							locked={[
								flowSettings.anchor,
								flowSettings.direction === 'between' ? flowSettings.end : null,
							].filter((name): name is string => name !== null)}
							onChange={onFilterChange}
						/>
					</SidebarSection>
				</div>
			</div>
		</aside>
	);
};

export default Sidebar;
